export type SpendScenario =
  | "dining"
  | "delivery_rideshare"
  | "grocery"
  | "fuel"
  | "online_shop"
  | "reimburse";

export type FrequencyBand = "none" | "low" | "mid" | "high";
export type BookingWill = "none" | "sometimes" | "all";
export type PayMethod = "apple" | "wechat" | "alipay" | "unionpay" | "card";

export type Profile = {
  version: 1;
  scenarios: SpendScenario[];
  flights: FrequencyBand;
  hotels: FrequencyBand;
  bookingWill: BookingWill;
  pay: PayMethod;
  completedAt: string;
  pinnedDefaultCardId?: string;
};

export const SCENARIO_OPTIONS: Array<{ id: SpendScenario; label: string; emoji: string }> = [
  { id: "dining", label: "餐饮（堂食/咖啡/奶茶）", emoji: "🍜" },
  { id: "delivery_rideshare", label: "外卖 & 网约车", emoji: "🛵" },
  { id: "grocery", label: "商超 & 便利店", emoji: "🛒" },
  { id: "fuel", label: "加油 & 停车", emoji: "⛽" },
  { id: "online_shop", label: "网购（淘宝/京东/拼多多）", emoji: "📦" },
  { id: "reimburse", label: "公司报销垫付", emoji: "💼" }
];

export const FLIGHT_OPTIONS: Array<{ id: FrequencyBand; label: string }> = [
  { id: "none", label: "没坐过" },
  { id: "low", label: "1-3 次" },
  { id: "mid", label: "4-10 次" },
  { id: "high", label: "10+ 次" }
];

export const HOTEL_OPTIONS: Array<{ id: FrequencyBand; label: string }> = [
  { id: "none", label: "没住过" },
  { id: "low", label: "1-5 次" },
  { id: "mid", label: "6+ 次" }
];

export const BOOKING_OPTIONS: Array<{ id: BookingWill; label: string }> = [
  { id: "none", label: "一般不约" },
  { id: "sometimes", label: "看心情" },
  { id: "all", label: "想约满" }
];

export const PAY_OPTIONS: Array<{ id: PayMethod; label: string }> = [
  { id: "apple", label: "Apple Pay" },
  { id: "wechat", label: "微信支付" },
  { id: "alipay", label: "支付宝" },
  { id: "unionpay", label: "云闪付" },
  { id: "card", label: "插卡 / 挥卡" }
];

const STORAGE_KEY = "mofaxiaoka.mvp.profile.v1";

export function emptyProfileDraft(): Omit<Profile, "completedAt"> {
  return {
    version: 1,
    scenarios: [],
    flights: "low",
    hotels: "low",
    bookingWill: "sometimes",
    pay: "apple"
  };
}

export function loadProfile(): Profile | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Profile;
    if (parsed?.version === 1 && Array.isArray(parsed.scenarios)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveProfile(profile: Profile): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

export function clearProfile(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
}

const SELECTED_KEY = "mofaxiaoka.mvp.selected.v1";

export function loadSelectedIds(): string[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(SELECTED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function saveSelectedIds(ids: string[]): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(SELECTED_KEY, JSON.stringify(ids));
}

const CUSTOM_KEY = "mofaxiaoka.mvp.customCards.v1";
const REQUEST_KEY = "mofaxiaoka.mvp.cardRequests.v1";
const WELCOMED_KEY = "mofaxiaoka.mvp.welcomed.v1";

export function isWelcomed(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(WELCOMED_KEY) === "1";
}

export function markWelcomed(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(WELCOMED_KEY, "1");
}

export function resetWelcomed(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(WELCOMED_KEY);
}

export type CustomCard = {
  id: string;
  name: string;
  issuer: string;
  level: string;
  annualFeeText: string;
  reportedAt: string;
  remoteId?: string;
};

export function loadCustomCards(): CustomCard[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is CustomCard =>
        x && typeof x.id === "string" && typeof x.name === "string"
    );
  } catch {
    return [];
  }
}

export function saveCustomCards(cards: CustomCard[]): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(CUSTOM_KEY, JSON.stringify(cards));
}

export function clearCustomCards(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(CUSTOM_KEY);
}

export type CardRequest = {
  id: string;
  cardName: string;
  issuer: string;
  level: string;
  annualFeeText: string;
  email: string;
  note: string;
  status: "queued" | "submitted" | "failed";
  createdAt: string;
  remoteId?: string;
};

export function loadCardRequests(): CardRequest[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(REQUEST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveCardRequests(reqs: CardRequest[]): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(REQUEST_KEY, JSON.stringify(reqs));
}

export function recordCardRequest(req: Omit<CardRequest, "id" | "createdAt" | "status">): CardRequest {
  const item: CardRequest = {
    ...req,
    id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    status: "queued"
  };
  const all = loadCardRequests();
  all.push(item);
  saveCardRequests(all);
  return item;
}

export async function submitCardRequest(item: CardRequest): Promise<CardRequest> {
  if (!item.email || !item.email.includes("@")) {
    return item;
  }
  try {
    const res = await fetch("/api/saas/card-request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: item.email,
        cardName: item.cardName,
        issuer: item.issuer,
        note: [item.level, item.annualFeeText, item.note].filter(Boolean).join(" · ").slice(0, 160),
        requestType: "missing_card"
      })
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const json = await res.json();
    const remoteId = typeof json?.id === "string" ? json.id : undefined;
    const all = loadCardRequests().map(x =>
      x.id === item.id ? { ...x, status: "submitted" as const, remoteId } : x
    );
    saveCardRequests(all);
    return { ...item, status: "submitted", remoteId };
  } catch {
    const all = loadCardRequests().map(x =>
      x.id === item.id ? { ...x, status: "failed" as const } : x
    );
    saveCardRequests(all);
    return { ...item, status: "failed" };
  }
}
